/**
 * SpreadsheetSettings.tsx - User preference settings for Spreadsheet view
 * 
 * CHANGE HISTORY:
 * - 251109_2148 [pending] danielbeer: Initial creation - settings popover with persistence
 * 
 * Features:
 * - Gear button with popover
 * - Toggleable UX settings
 * - Context-specific storage
 * - Save as default option
 * - Aggregatr integration for persistence
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAggregatr, useInterfacr } from '@opcentral/dependr';

export interface SpreadsheetUserSettings {
  // Display
  wrapText: boolean;
  autoHeight: boolean;
  showGridLines: boolean;
  
  // Scrolling
  alwaysShowHorizontalScroll: boolean;
  alwaysShowVerticalScroll: boolean;
  
  // Columns
  minColumnWidth: number;
  defaultColumnWidth: number;
  resizableColumns: boolean;
  
  // Features
  enableSorting: boolean;
  enableFiltering: boolean;
  enableEditing: boolean;
  enablePagination: boolean;
  pageSize: number;
  
  // Export
  enableExport: boolean;
  exportFormats: ('csv' | 'json' | 'xlsx')[];
  
  // Behavior
  rowMode: boolean;
  cellPadding: string;
  lineHeight: string;
}

export const defaultSettings: SpreadsheetUserSettings = {
  wrapText: true,
  autoHeight: true,
  showGridLines: true,
  alwaysShowHorizontalScroll: true,
  alwaysShowVerticalScroll: true,
  minColumnWidth: 150,
  defaultColumnWidth: 200,
  resizableColumns: true,
  enableSorting: true,
  enableFiltering: true,
  enableEditing: true,
  enablePagination: true,
  pageSize: 50,
  enableExport: true,
  exportFormats: ['csv', 'json', 'xlsx'],
  rowMode: false,
  cellPadding: '8px',
  lineHeight: '1.5'
};

interface SpreadsheetSettingsProps {
  contextId: string; // e.g., 'office-viewer-xlsx', 'data-table-view'
  currentSettings: SpreadsheetUserSettings;
  onSettingsChange: (settings: SpreadsheetUserSettings) => void;
}

export const SpreadsheetSettings: React.FC<SpreadsheetSettingsProps> = ({
  contextId,
  currentSettings,
  onSettingsChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<SpreadsheetUserSettings>(currentSettings);
  const popoverRef = useRef<HTMLDivElement>(null);
  const aggregatr = useAggregatr();
  const { interfacr } = useInterfacr();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSettingChange = (key: keyof SpreadsheetUserSettings, value: any) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    onSettingsChange(updated);
    
    // Auto-save to context-specific storage
    saveSettings(updated, false);
  };

  const saveSettings = async (settingsToSave: SpreadsheetUserSettings, asDefault: boolean): Promise<void> => {
    try {
      // Save context-specific settings
      await aggregatr.update(
        'spreadsheet-settings',
        contextId,
        settingsToSave
      ).catch(() => {
        // If doesn't exist, create it
        return aggregatr.create('spreadsheet-settings', {
          id: contextId,
          ...settingsToSave
        });
      });

      // If saving as default, also save to global default
      if (asDefault) {
        await aggregatr.update(
          'spreadsheet-settings',
          'default',
          settingsToSave
        ).catch(() => {
          return aggregatr.create('spreadsheet-settings', {
            id: 'default',
            ...settingsToSave
          });
        });
      }
    } catch (error) {
      console.error('Failed to save spreadsheet settings:', error);
    }
  };

  const handleSaveAsDefault = async () => {
    await saveSettings(settings, true);
    // Use Interfacr to route to Promptr
    await interfacr.execute({
      type: 'notification',
      operation: 'success',
      params: {
        message: 'Settings saved as default for all spreadsheet views',
        duration: 3000
      }
    });
  };

  const handleResetToDefault = async () => {
    setSettings(defaultSettings);
    onSettingsChange(defaultSettings);
    await saveSettings(defaultSettings, false);
  };

  return (
    <div className="spreadsheet-settings" style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="spreadsheet-settings-button"
        title="Spreadsheet Settings"
        style={{
          background: 'transparent',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '6px 10px',
          cursor: 'pointer',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        ⚙️ Settings
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className="spreadsheet-settings-popover"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '16px',
            minWidth: '320px',
            maxHeight: '600px',
            overflowY: 'auto',
            zIndex: 1000
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
            Spreadsheet Settings
          </h3>

          {/* Display Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#666' }}>
              Display
            </h4>
            <SettingToggle
              label="Wrap Text"
              checked={settings.wrapText}
              onChange={(checked) => handleSettingChange('wrapText', checked)}
            />
            <SettingToggle
              label="Auto Height Rows"
              checked={settings.autoHeight}
              onChange={(checked) => handleSettingChange('autoHeight', checked)}
            />
            <SettingToggle
              label="Show Grid Lines"
              checked={settings.showGridLines}
              onChange={(checked) => handleSettingChange('showGridLines', checked)}
            />
          </div>

          {/* Scrolling Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#666' }}>
              Scrolling
            </h4>
            <SettingToggle
              label="Always Show Horizontal Scroll"
              checked={settings.alwaysShowHorizontalScroll}
              onChange={(checked) => handleSettingChange('alwaysShowHorizontalScroll', checked)}
            />
            <SettingToggle
              label="Always Show Vertical Scroll"
              checked={settings.alwaysShowVerticalScroll}
              onChange={(checked) => handleSettingChange('alwaysShowVerticalScroll', checked)}
            />
          </div>

          {/* Column Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#666' }}>
              Columns
            </h4>
            <SettingNumber
              label="Min Column Width"
              value={settings.minColumnWidth}
              onChange={(value) => handleSettingChange('minColumnWidth', value)}
              min={50}
              max={500}
              step={10}
            />
            <SettingToggle
              label="Resizable Columns"
              checked={settings.resizableColumns}
              onChange={(checked) => handleSettingChange('resizableColumns', checked)}
            />
          </div>

          {/* Feature Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#666' }}>
              Features
            </h4>
            <SettingToggle
              label="Enable Sorting"
              checked={settings.enableSorting}
              onChange={(checked) => handleSettingChange('enableSorting', checked)}
            />
            <SettingToggle
              label="Enable Filtering"
              checked={settings.enableFiltering}
              onChange={(checked) => handleSettingChange('enableFiltering', checked)}
            />
            <SettingToggle
              label="Enable Editing"
              checked={settings.enableEditing}
              onChange={(checked) => handleSettingChange('enableEditing', checked)}
            />
            <SettingToggle
              label="Enable Pagination"
              checked={settings.enablePagination}
              onChange={(checked) => handleSettingChange('enablePagination', checked)}
            />
            {settings.enablePagination && (
              <SettingNumber
                label="Page Size"
                value={settings.pageSize}
                onChange={(value) => handleSettingChange('pageSize', value)}
                min={10}
                max={500}
                step={10}
              />
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            paddingTop: '16px', 
            borderTop: '1px solid #eee',
            marginTop: '16px'
          }}>
            <button
              onClick={handleSaveAsDefault}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              💾 Save as Default
            </button>
            <button
              onClick={handleResetToDefault}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              🔄 Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const SettingToggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    padding: '8px 0',
    cursor: 'pointer'
  }}>
    <span style={{ fontSize: '13px' }}>{label}</span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
    />
  </label>
);

const SettingNumber: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
}> = ({ label, value, onChange, min, max, step }) => (
  <div style={{ padding: '8px 0' }}>
    <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
      {label}: {value}
    </label>
    <input
      type="range"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      min={min}
      max={max}
      step={step}
      style={{ width: '100%', cursor: 'pointer' }}
    />
  </div>
);

export default SpreadsheetSettings;
